import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resumeTenant, suspendTenant, type Tenant } from "./tenantsApi";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Modal } from "@web/ui";

// ADR-0014 §7.4 (product-owner decision f3ca7f9e) — FREEZING a customer tenant, and thawing it again.
//
// A freeze shuts every door of one tenant: nobody signs in, no capability token is issued, and the
// background sweeps that work on that tenant's behalf stand still. It deletes NOTHING — the rows, the
// audit chain and the encryption keys are untouched — which is the whole reason it is reversible and the
// reason the confirmation can state its effects as facts rather than warnings. The final deletion is a
// different operation and is not on this page (task 386c1494).
//
// TWO ASYMMETRIES, both deliberate:
//
//   * FREEZING asks for a typed confirmation (the slug) and a written reason; THAWING asks for neither.
//     A freeze locks a customer's whole organisation out of the product, and the slug is the one string
//     that proves the operator is looking at the tenant they think they are — the page is reached from a
//     list where four customers can read alike. Thawing only re-opens doors, so a confirmation step there
//     would be ceremony with nothing to protect.
//
//   * The REASON is required HERE and optional in the API. The backend accepts an empty one because it
//     refuses to invent a validation rule a future caller might not share; this console demands it because
//     "why was this customer shut" is the one fact nobody can reconstruct afterwards — the audit row
//     carries who and when by itself.
//
// IDEMPOTENCE is the backend's: freezing a frozen tenant answers 200 and changes neither the timestamp nor
// the stored reason, so a double-submit cannot rewrite the record of why a customer was shut.

/** The confirmation's own state. Absent means the section is in its resting, no-dialog state. */
type Dialog = { reason: string; confirm: string; submitting: boolean; error: string | null };

const EMPTY_DIALOG: Dialog = { reason: "", confirm: "", submitting: false, error: null };

/**
 * The effects the confirmation lists, as i18n keys. A list and not one paragraph: an operator reading a
 * confirmation reads the bullets, and "data stays untouched" is the one of the three they most need to
 * have seen before they answer.
 */
const FREEZE_EFFECTS = [
  "tenants.freeze.effectSignIn",
  "tenants.freeze.effectSchedules",
  "tenants.freeze.effectData",
] as const;

/** The i18n key for a freeze/thaw failure, by what the operator has to do about it. */
function freezeErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.freeze.errorNetwork";
  switch (err.status) {
    // The tenant is gone, or it is the Verso operator tenant — which can never be frozen, and is the only
    // way this status reaches a page the operator opened from the customer directory.
    case 404:
      return "tenants.freeze.error404";
    case 422:
      return "tenants.freeze.error422";
    default:
      return "tenants.freeze.errorNetwork";
  }
}

export function TenantFreezeSection({
  tenant,
  headingId,
  onChanged,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /** The tenant's lifecycle moved: the page adopts the server's new state and says what happened. */
  onChanged: (saved: Tenant, what: "suspended" | "resumed") => void;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  /** The thaw has no dialog, so its in-flight state and its failure live out here. */
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const frozen = tenant.suspendedAt !== null;

  const onFreeze = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!dialog || dialog.submitting) return;

      const reason = dialog.reason.trim();
      if (reason.length === 0) {
        setDialog((d) => d && { ...d, error: t("tenants.freeze.reasonRequired") });
        return;
      }

      // Functional updates throughout: what the operator typed is the part of this state that must not be
      // overwritten by a stale closure, and a refusal has to leave the dialog exactly as they left it so
      // they can correct one field rather than re-type both.
      setDialog((d) => d && { ...d, submitting: true, error: null });
      suspendTenant(tenant.id, { reason })
        .then((saved) => {
          setDialog(null);
          onChanged(saved, "suspended");
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          setDialog((d) => d && { ...d, submitting: false, error: t(freezeErrorKey(err)) });
        });
    },
    [dialog, tenant.id, onChanged, t],
  );

  const onResume = useCallback(() => {
    if (resuming) return;
    setError(null);
    setResuming(true);
    resumeTenant(tenant.id)
      .then((saved) => onChanged(saved, "resumed"))
      .catch((err: unknown) => {
        if (err instanceof SessionExpiredError) return;
        setError(t(freezeErrorKey(err)));
      })
      .finally(() => setResuming(false));
  }, [resuming, tenant.id, onChanged, t]);

  const titleId = "tenant-freeze-title";
  const bodyId = "tenant-freeze-body";
  // The typed confirmation, matched EXACTLY — not trimmed and not case-folded. The slug is lowercase by
  // construction, so an exact match costs a careful operator nothing and is the only comparison that
  // makes the control do what it says.
  const confirmed = dialog?.confirm === tenant.slug;

  return (
    <>
      <h2 id={headingId}>{t("tenants.freeze.title")}</h2>

      {frozen ? (
        <>
          <p className="freeze__intro" data-testid="tenant-freeze-state">
            {t("tenants.freeze.frozenIntro", {
              date: formatDate(new Date(tenant.suspendedAt!)),
            })}
          </p>
          {tenant.suspensionReason && (
            <p className="freeze__reason" data-testid="tenant-freeze-reason-shown">
              {t("tenants.freeze.reasonShown", { reason: tenant.suspensionReason })}
            </p>
          )}
          {error && (
            <p className="field-error" role="alert" data-testid="tenant-freeze-error">
              {error}
            </p>
          )}
          <div className="tenant-section__actions">
            {/* No typed confirmation: thawing re-opens doors and destroys nothing, so there is nothing
                here for a second step to protect. */}
            <button
              type="button"
              className="btn btn--primary"
              onClick={onResume}
              disabled={resuming}
              data-testid="tenant-resume"
            >
              {resuming ? t("tenants.freeze.unfreezing") : t("tenants.freeze.unfreeze")}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="freeze__intro">{t("tenants.freeze.intro")}</p>
          {error && (
            <p className="field-error" role="alert" data-testid="tenant-freeze-error">
              {error}
            </p>
          )}
          <div className="tenant-section__actions">
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => {
                setError(null);
                setDialog(EMPTY_DIALOG);
              }}
              data-testid="tenant-freeze-open"
            >
              {t("tenants.freeze.open")}
            </button>
          </div>
        </>
      )}

      {dialog && (
        <Modal
          role="alertdialog"
          className="freeze__dialog"
          labelledBy={titleId}
          describedBy={bodyId}
          testId="tenant-freeze-dialog"
          onClose={() => setDialog(null)}
          closeDisabled={dialog.submitting}
          initialFocus={reasonRef}
        >
          <h2 id={titleId}>{t("tenants.freeze.confirmTitle", { tenant: tenant.slug })}</h2>
          <ul id={bodyId} className="freeze__effects">
            {FREEZE_EFFECTS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>

          <form className="form" onSubmit={onFreeze} noValidate>
            <label htmlFor="tenant-freeze-reason">{t("tenants.freeze.reasonLabel")}</label>
            {/* No native `required`: the check is in the submit handler so the form shows the app's own
                message rather than a browser tooltip, as every other form on this console does. */}
            <textarea
              id="tenant-freeze-reason"
              ref={reasonRef}
              rows={3}
              value={dialog.reason}
              onChange={(e) => setDialog((d) => d && { ...d, reason: e.target.value })}
              disabled={dialog.submitting}
              data-testid="tenant-freeze-reason"
            />

            <label htmlFor="tenant-freeze-confirm">
              {t("tenants.freeze.confirmLabel", { tenant: tenant.slug })}
            </label>
            <input
              id="tenant-freeze-confirm"
              type="text"
              value={dialog.confirm}
              onChange={(e) => setDialog((d) => d && { ...d, confirm: e.target.value })}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={dialog.submitting}
              data-testid="tenant-freeze-confirm"
            />

            {dialog.error && (
              <p className="field-error" role="alert" data-testid="tenant-freeze-dialog-error">
                {dialog.error}
              </p>
            )}

            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                onClick={() => setDialog(null)}
                disabled={dialog.submitting}
                data-testid="tenant-freeze-cancel"
              >
                {t("tenants.cancel")}
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                disabled={!confirmed || dialog.submitting}
                data-testid="tenant-freeze-submit"
              >
                {dialog.submitting ? t("tenants.freeze.freezing") : t("tenants.freeze.submit")}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
